/**
 * @file: 单个cube坐标扫描, 单位cm
 * @author: xiaolong.huang@dmall.com
 */

import * as CubeTree from '../cubeTree/index';
import * as Toolkit from '../toolkit/index';
import FrontCubeData from '../frontCubeData/index';
import Config from '../config/index';
import Utils from '../utils/util';
import Store from './store/index';
import { containSubGoodsWithPosWidthSizeByCubeId } from '../UIToolkit/goods/index';

const defaultLayerWidth: PhysicsMeasurement = Config.get({
    moduleName: 'layer',
    key: 'defaultWidth'
});

export function sanCube(cubeId: string): PhysicsCoordinate|null {
    if (CubeTree.isFreezer(cubeId)) {
        return scanFreezer(cubeId);
    }

    if (CubeTree.isCabinet(cubeId)) {
        return scanCabinet(cubeId);
    }

    if (CubeTree.isShelf(cubeId)) {
        return scanShelf(cubeId);
    }

    if (CubeTree.isDishrack(cubeId)
        || CubeTree.isBeam(cubeId)
        || CubeTree.isRoof(cubeId)
        || CubeTree.isLayer(cubeId)) {
        return scanPartition(cubeId);
    }

    if (CubeTree.isTextarea(cubeId)) {
        return scanTextarea(cubeId);
    }

    if (CubeTree.isBafflePlate(cubeId)) {
        return scanBafflePlate(cubeId);
    }

    if (CubeTree.isGoods(cubeId)) {
        return scanGoods(cubeId);
    }

    return null;
}

// 扫描货架坐标
function scanShelf(cubeId: CubeId): PhysicsCoordinate {
    let prevShelf: CubeId = CubeTree.findPrevShelf(cubeId);
    let left: PhysicsMeasurement = 0;

    if (prevShelf) {
        let prev: PhysicsCoordinate = Store.getPhysicsCoordinate(prevShelf);
        let leftStart: number = Utils.math(prev.left, prev.width, '+');

        left = leftStart;
    }

    let bottom: PhysicsMeasurement = 0;
    let width: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'width');
    let height: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'height');

    return {
        left,
        bottom,
        width,
        height
    };
}

// 扫描卧柜
function scanFreezer(cubeId: CubeId): PhysicsCoordinate {
    let prevFreezer: CubeId = CubeTree.findPrevFreezer(cubeId);
    let left: PhysicsMeasurement = 0;

    if (prevFreezer) {
        let prev: PhysicsCoordinate = Store.getPhysicsCoordinate(prevFreezer);
        let leftStart: number = Utils.math(prev.left, prev.width, '+');

        left = leftStart;
    }

    let bottom: PhysicsMeasurement = 0;
    let width: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'width');
    let height: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'depth');

    return {
        left,
        bottom,
        width,
        height
    };
}

// 扫描卧柜层板
function scanCabinet(cubeId: CubeId): PhysicsCoordinate {
    let freezerId: CubeId = CubeTree.findParentIdByCubeId(cubeId);
    let freezerCoordinate: PhysicsCoordinate = Store.getPhysicsCoordinate(freezerId);

    return {
        left: freezerCoordinate.left,
        bottom: freezerCoordinate.bottom,
        width: freezerCoordinate.width,
        height: freezerCoordinate.height
    };
}

// 扫描层板坐标
function scanPartition(cubeId: CubeId): PhysicsCoordinate {
    let width: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'moduleWidth');
    let height: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'moduleHeight');

    // 层板默认宽度30cm
    width = width < 0 ? defaultLayerWidth : width;

    if (CubeTree.isInStage(cubeId)) {
        // 画布中的层板
        return {
            width,
            height,
            left: FrontCubeData.getNumberValue(cubeId, 'posX'),
            bottom: FrontCubeData.getNumberValue(cubeId, 'posY')
        };
    }

    let parentId: CubeId = CubeTree.findParentIdByCubeId(cubeId);
    let parentCoordinate: PhysicsCoordinate = Store.getPhysicsCoordinate(parentId);
    let left: PhysicsMeasurement = Utils.math(parentCoordinate.left, FrontCubeData.getNumberValue(cubeId, 'posX'), '+');
    let bottom: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'posY');

    return {
        left,
        bottom,
        width,
        height
    }
}

// 扫描文本框
function scanTextarea(cubeId: CubeId): PhysicsCoordinate {
    let width: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'width');
    let height: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'height');

    if (CubeTree.isInStage(cubeId)) {
        // 画布中的文本框
        return {
            width,
            height,
            left: FrontCubeData.getNumberValue(cubeId, 'posX'),
            bottom: FrontCubeData.getNumberValue(cubeId, 'posY')
        };
    }

    let parentId: CubeId = CubeTree.findParentIdByCubeId(cubeId);
    let parentCoordinate: PhysicsCoordinate = Store.getPhysicsCoordinate(parentId);
    let left: PhysicsMeasurement = Utils.math(parentCoordinate.left, FrontCubeData.getNumberValue(cubeId, 'posX'), '+');
    let bottomLine: number = Utils.math(parentCoordinate.bottom, parentCoordinate.height, '+');
    let bottom: PhysicsMeasurement = Utils.math(bottomLine, FrontCubeData.getNumberValue(cubeId, 'posY'), '+');

    if (CubeTree.isCabinet(parentId)) {
        bottom = FrontCubeData.getNumberValue(cubeId, 'posY');
    }

    if (CubeTree.isBeam(parentId)) {
        let dropHeight: number = Utils.math(height, parentCoordinate.height, '-');

        bottom = Utils.math(parentCoordinate.bottom, dropHeight, '-');
    }

    return {
        left,
        bottom,
        width,
        height
    }
}

// 扫描隔板
function scanBafflePlate(cubeId: CubeId): PhysicsCoordinate {
    let width: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'width');
    let height: PhysicsMeasurement = FrontCubeData.getNumberValue(cubeId, 'height');

    if (CubeTree.isInStage(cubeId)) {
        // 画布中的隔板
        return {
            width,
            height,
            left: FrontCubeData.getNumberValue(cubeId, 'posX'),
            bottom: FrontCubeData.getNumberValue(cubeId, 'posY')
        };
    }

    let parentId: CubeId = CubeTree.findParentIdByCubeId(cubeId);
    let parentCoordinate: PhysicsCoordinate = Store.getPhysicsCoordinate(parentId);
    let left: PhysicsMeasurement = Utils.math(parentCoordinate.left, FrontCubeData.getNumberValue(cubeId, 'posX'), '+');
    let bottomLine: number = Utils.math(parentCoordinate.bottom, parentCoordinate.height, '+');
    let bottom: PhysicsMeasurement = Utils.math(bottomLine, FrontCubeData.getNumberValue(cubeId, 'posY'), '+');

    return {
        left,
        bottom,
        width,
        height
    }
}

// 扫描商品
function scanGoods(cubeId: CubeId): PhysicsCoordinate {
    let goodsSize: Size = containSubGoodsWithPosWidthSizeByCubeId(cubeId);
    let width: PhysicsMeasurement = goodsSize.width;
    let height: PhysicsMeasurement = goodsSize.height;

    if (CubeTree.isInStage(cubeId)) {
        // 画布中的商品
        return {
            width,
            height,
            left: FrontCubeData.getNumberValue(cubeId, 'posX'),
            bottom: FrontCubeData.getNumberValue(cubeId, 'posY')
        };
    }

    let parentId: CubeId = CubeTree.findParentIdByCubeId(cubeId);
    let parentCoordinate: PhysicsCoordinate = Store.getPhysicsCoordinate(parentId);
    let left: PhysicsMeasurement = Utils.math(parentCoordinate.left, FrontCubeData.getNumberValue(cubeId, 'posX'), '+');
    let bottomLine: number = Utils.math(parentCoordinate.bottom, parentCoordinate.height, '+');
    let bottom: PhysicsMeasurement = Utils.math(bottomLine, FrontCubeData.getNumberValue(cubeId, 'posY'), '+');

    if (CubeTree.isCabinet(parentId)) {
        bottom = FrontCubeData.getNumberValue(cubeId, 'posY');
    }

    if (CubeTree.isBeam(parentId)) {
        let dropHeight: number = Utils.math(height, parentCoordinate.height, '-');

        bottom = Utils.math(parentCoordinate.bottom, dropHeight, '-');
    }

    return {
        left,
        bottom,
        width,
        height
    }
}
